import type {
  STREAM_EVENT,
  StreamedProgressiveUiComponentData,
  StreamedProgressiveUiData,
} from 'tripPlanner/api/types'
import type { StreamEvent } from 'tripPlanner/utils/streamEventParser'
import {
  isStreamedCardOption,
  isStreamedCarouselOfferId,
  isStreamedCarouselOccupancy,
  isStreamedPillOption,
  isStreamedTestimonialReview,
} from 'tripPlanner/utils/streamedProgressiveUi'

type ProgressiveUiPatchEvent = Extract<StreamEvent, { event: typeof STREAM_EVENT.UI_PART_PATCH }>
type ProgressiveUiCompletedEvent = Extract<StreamEvent, { event: typeof STREAM_EVENT.UI_PART_COMPLETED }>

interface StreamedUiPartAdapter {
  applyPatch: (
    data: StreamedProgressiveUiComponentData,
    event: ProgressiveUiPatchEvent,
  ) => StreamedProgressiveUiComponentData | undefined
  complete?: (
    data: StreamedProgressiveUiComponentData,
    event: ProgressiveUiCompletedEvent,
  ) => StreamedProgressiveUiComponentData | undefined
}

function isSingleSegmentPath(path: Array<string>, segment: string): boolean {
  return path.length === 1 && path[0] === segment
}

function withStreamedMetadata(
  data: StreamedProgressiveUiData,
  partId: string,
): StreamedProgressiveUiComponentData {
  return { ...data, streamedPartId: partId, streamingStatus: 'complete' }
}

const cardsListAdapter: StreamedUiPartAdapter = {
  applyPatch: (data, event) => {
    if (data.type !== 'cards_list') return undefined

    if (
      event.op === 'append' &&
      isSingleSegmentPath(event.path, 'options') &&
      isStreamedCardOption(event.value)
    ) {
      return {
        ...data,
        streamingStatus: 'streaming',
        options: [...data.options, event.value],
      }
    }

    if (
      event.op === 'set' &&
      isSingleSegmentPath(event.path, 'select') &&
      (event.value === 'single' || event.value === 'multiple')
    ) {
      return {
        ...data,
        streamingStatus: 'streaming',
        select: event.value,
      }
    }

    if (
      event.op === 'set' &&
      isSingleSegmentPath(event.path, 'categories') &&
      (event.value === 'generic' || event.value === 'places' || event.value === 'locations')
    ) {
      return {
        ...data,
        streamingStatus: 'streaming',
        categories: event.value,
      }
    }

    return undefined
  },
}

const pillsListAdapter: StreamedUiPartAdapter = {
  applyPatch: (data, event) => {
    if (data.type !== 'pills_list') return undefined

    if (
      event.op === 'append' &&
      isSingleSegmentPath(event.path, 'options') &&
      isStreamedPillOption(event.value)
    ) {
      return {
        ...data,
        streamingStatus: 'streaming',
        options: [...data.options, event.value],
      }
    }

    if (
      event.op === 'set' &&
      isSingleSegmentPath(event.path, 'select') &&
      (event.value === 'single' || event.value === 'multiple')
    ) {
      return {
        ...data,
        streamingStatus: 'streaming',
        select: event.value,
      }
    }

    return undefined
  },
}

const testimonialAdapter: StreamedUiPartAdapter = {
  applyPatch: (data, event) => {
    if (data.type !== 'testimonial') return undefined

    if (
      event.op === 'append' &&
      isSingleSegmentPath(event.path, 'reviews') &&
      isStreamedTestimonialReview(event.value)
    ) {
      return {
        ...data,
        streamingStatus: 'streaming',
        reviews: [...data.reviews, event.value],
      }
    }

    return undefined
  },
}

const carouselAdapter: StreamedUiPartAdapter = {
  applyPatch: (data, event) => {
    if (data.type !== 'carousel') return undefined

    if (
      event.op === 'append' &&
      isSingleSegmentPath(event.path, 'offerIds') &&
      isStreamedCarouselOfferId(event.value)
    ) {
      return {
        ...data,
        streamingStatus: 'streaming',
        offerIds: [...data.offerIds, event.value],
      }
    }

    if (
      event.op === 'set' &&
      isSingleSegmentPath(event.path, 'title') &&
      typeof event.value === 'string'
    ) {
      return {
        ...data,
        streamingStatus: 'streaming',
        title: event.value,
      }
    }

    if (
      event.op === 'set' &&
      isSingleSegmentPath(event.path, 'occupancy') &&
      isStreamedCarouselOccupancy(event.value)
    ) {
      return {
        ...data,
        streamingStatus: 'streaming',
        occupancy: event.value,
      }
    }

    return undefined
  },
  complete: (data, event) => {
    if (data.type !== 'carousel' || event.data.type !== 'carousel') return undefined

    if (event.data.occupancy == null && isStreamedCarouselOccupancy(data.occupancy)) {
      return withStreamedMetadata(
        { ...event.data, occupancy: data.occupancy },
        event.partId,
      )
    }

    return withStreamedMetadata(event.data, event.partId)
  },
}

const STREAMED_UI_PART_ADAPTERS: Record<StreamedProgressiveUiComponentData['type'], StreamedUiPartAdapter> = {
  cards_list: cardsListAdapter,
  carousel: carouselAdapter,
  pills_list: pillsListAdapter,
  testimonial: testimonialAdapter,
}

export function applyStreamedUiPartPatch(
  data: StreamedProgressiveUiComponentData,
  event: ProgressiveUiPatchEvent,
): StreamedProgressiveUiComponentData | undefined {
  return STREAMED_UI_PART_ADAPTERS[data.type].applyPatch(data, event)
}

export function completeStreamedUiPart(
  data: StreamedProgressiveUiComponentData,
  event: ProgressiveUiCompletedEvent,
): StreamedProgressiveUiComponentData | undefined {
  if (event.data.type !== data.type) return undefined

  return STREAMED_UI_PART_ADAPTERS[data.type].complete?.(data, event) ??
    withStreamedMetadata(event.data, event.partId)
}
